// App
import pathsManifest from 'app/shared/utils/pathsManifest';

// Misc / Utils
import dateUtils from 'app/shared/utils/dateUtils';
import omit from 'lodash/omit';
import queryUtils from 'app/shared/utils/queryUtils';
import stringHelper from 'app/shared/utils/stringHelper';

// Type definitions
interface PetDetail {
  type?: string;
}

interface RenterPetDetail {
  petType?: string;
}

interface RenterProfileApiData {
  moveInDate?: Date | Array<number>;
  moveInTimeFrame?: string;
  leaseLengthMonths?: number;
  leaseDuration?: number;
  incomeYearly?: number;
  numBedrooms?: number;
  numBeds?: number;
  numOccupants?: number;
  petDetails?: Array<PetDetail>;
  renterPetDetails?: RenterPetDetail;
  desiredPets?: string;
  creditScoreMin?: number;
  creditScoreMax?: number;
  desiredParking?: string;
}

interface RenterProfile {
  moveInDate?: Date;
  moveInTimeFrame?: string;
  leaseDuration?: number | null;
  incomeYearly?: number;
  numBeds?: number;
  numOccupants?: number;
  desiredPets?: string;
  creditScore?: string;
  creditScoreMin?: number;
  creditScoreMax?: number;
  desiredParking?: string;
}

interface RenterProfileQueryData {
  creditScoreMin?: number | string;
  creditScoreMax?: number | string;
  desiredParking?: string;
  desiredPets?: string;
  incomeYearly?: number | string;
  leaseDuration?: number | string;
  moveInDate?: Date;
  moveInTimeFrame?: string;
  numBeds?: number | string;
  numOccupants?: number | string;
}

interface RenterProfileApiQuery {
  moveInDate?: string;
  moveInTimeFrame?: string;
  leaseLengthMonths?: number;
  numOccupants?: number;
  incomeYearly?: number;
  creditScoreMin?: number;
  creditScoreMax?: number;
  numBedrooms?: number;
  desiredPets?: string;
  desiredParking?: string;
}

interface Subscriptions {
  stopListings?: boolean;
  stopOther?: boolean;
}

const userUtils = {
  mapRenterProfileApiToRedux(data: RenterProfileApiData = {}): RenterProfile {
    const renterProfile: RenterProfile = {};

    if (data?.moveInDate && Array.isArray(data.moveInDate)) {
      const [dateYear, dateMonth, dateDay] = data.moveInDate;
      renterProfile.moveInDate = new Date(`${dateYear}/${dateMonth}/${dateDay}`);
    } else if (data?.moveInDate && data.moveInDate instanceof Date) {
      renterProfile.moveInDate = data.moveInDate;
    }

    // Handle issue translating between data from Redux vs data from API
    const leaseLength = data.leaseLengthMonths || data.leaseDuration;

    renterProfile.moveInTimeFrame = data.moveInTimeFrame ? data.moveInTimeFrame.toLowerCase() : '';
    renterProfile.leaseDuration = leaseLength ? Number(leaseLength) : null;
    renterProfile.incomeYearly = data.incomeYearly;

    const numBedrooms =
      data.numBedrooms !== undefined && !isNaN(data.numBedrooms) && data.numBedrooms >= 0
        ? data.numBedrooms
        : data.numBeds;
    renterProfile.numBeds = numBedrooms;
    renterProfile.numOccupants = data.numOccupants;

    // TODO: delete petDetails related code when petDetails field is replcaed by renterPetDetails field on prod.
    const petDetails = data?.petDetails;
    const renterPetDetails = data?.renterPetDetails;
    if ((petDetails && petDetails?.length >= 1 && petDetails[0]?.type !== 'NONE') || data.desiredPets) {
      renterProfile.desiredPets = 'true';
    } else if (renterPetDetails && renterPetDetails?.petType && renterPetDetails?.petType !== 'NONE') {
      renterProfile.desiredPets = 'true';
    } else {
      renterProfile.desiredPets = 'false';
    }

    // Funky stuff to handle credit score API:
    if (data?.creditScoreMin && data?.creditScoreMax) {
      const creditRange = `${data.creditScoreMin}-${data.creditScoreMax}`;
      renterProfile.creditScore = creditRange;
      renterProfile.creditScoreMin = data.creditScoreMin;
      renterProfile.creditScoreMax = data.creditScoreMax;
    }

    renterProfile.desiredParking = data.desiredParking;

    return renterProfile;
  },
  mapRenterProfileDataToApi(data: RenterProfileQueryData = {}): RenterProfileApiQuery {
    const {
      creditScoreMin,
      creditScoreMax,
      desiredParking,
      desiredPets,
      incomeYearly,
      leaseDuration,
      moveInDate,
      moveInTimeFrame,
      numBeds,
      numOccupants,
    } = data;
    const queryObj: RenterProfileApiQuery = {};

    const shouldAddLeadingZeroForDate = (value: number): string | number => {
      if (value < 10) {
        return `0${value}`;
      }

      return value;
    };

    if (moveInDate) {
      if (dateUtils.dateIsValid(moveInDate)) {
        const formatDate = [
          moveInDate.getFullYear(),
          shouldAddLeadingZeroForDate(moveInDate.getMonth() + 1),
          shouldAddLeadingZeroForDate(moveInDate.getDate()),
        ].join('-');
        queryObj.moveInDate = formatDate;
      }
    }

    if (moveInTimeFrame) {
      queryObj.moveInTimeFrame = 'flexible';
    }

    if (leaseDuration) {
      queryObj.leaseLengthMonths = Number(leaseDuration);
    }

    if (numOccupants) {
      queryObj.numOccupants = Number(numOccupants);
    }

    if (incomeYearly) {
      queryObj.incomeYearly = Number(incomeYearly);
    }

    if (creditScoreMin) {
      queryObj.creditScoreMin = Number(creditScoreMin);
    }

    if (creditScoreMax) {
      queryObj.creditScoreMax = Number(creditScoreMax);
    }

    if (numBeds !== undefined && !isNaN(Number(numBeds)) && Number(numBeds) >= 0) {
      queryObj.numBedrooms = Number(numBeds);
    }

    if (desiredPets) {
      queryObj.desiredPets = desiredPets;
    }

    if (desiredParking) {
      queryObj.desiredParking = desiredParking;
    }

    return queryObj;
  },

  isRep(roles: Array<string>): boolean {
    return roles.indexOf('rep') > -1;
  },
  isAdmin(roles: Array<string>): boolean {
    return roles.indexOf('admin') > -1;
  },
  hasActiveSubscriptions(subscriptions: Subscriptions): boolean {
    if (subscriptions.stopListings && subscriptions.stopOther) {
      return false;
    }

    return true;
  },
  successLoginRedirect(transitionPath?: string): boolean | void {
    const queryParams = stringHelper.getUrlQueryParams();

    if (queryParams === false) {
      return false;
    }

    transitionPath = transitionPath || (queryParams.redirect as string);
    if (transitionPath && transitionPath[0] !== '/') {
      transitionPath = '/';
    }
    const csr = queryParams.csr;
    const restOfQueryParams = omit(queryParams as Record<string, unknown>, 'redirect') as Record<
      string,
      string | number | boolean | undefined
    >;

    if (transitionPath && csr) {
      const pathWithQuery = transitionPath + queryUtils.stringify(restOfQueryParams);
      window.router.transitionTo(pathWithQuery);
      return;
    } else if (transitionPath) {
      const pathWithQuery = transitionPath + queryUtils.stringify(restOfQueryParams);
      (window.location as unknown as string) = window.location.origin + pathWithQuery;
      return;
    } else if (window.location.pathname === pathsManifest.LoginPage) {
      window.router.transitionTo(pathsManifest.UserDashboardPage);
      return;
    } else if (window.location.pathname === pathsManifest.UpdatePasswordPage) {
      window.router.transitionTo('/');
      return;
    } else {
      return false;
    }
  },
};

export default userUtils;
